// Orenza slide-in form drawer. Used for master create + edit.
// Esc closes (after confirming if dirty); Ctrl+A or Ctrl+S saves.
// v0.9.36: widened default to 640px + responsive clamp so it feels like a real workspace,
// not a stuck sidebar. Supports size="md" | "lg" | "xl" for common breakpoints.
// Also stripes the header with a Cobalt→Citrus gradient bar so it reads as a distinct surface.

import { useEffect, useRef, type ReactNode, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Button } from '@/components/primitives/Button';
import { KbdChip } from '@/components/primitives/KbdChip';
import { cn } from '@/lib/cn';

export type FormDrawerSize = 'md' | 'lg' | 'xl';

export interface FormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  title: string;
  subtitle?: string;
  saving?: boolean;
  saveDisabled?: boolean;
  error?: string | null;
  dirty?: boolean;
  /** Preferred: use `size`. Kept for backward compatibility. */
  width?: string;
  size?: FormDrawerSize;
  children: ReactNode;
}

const SIZE_STYLES: Record<FormDrawerSize, string> = {
  md: 'w-full sm:w-[560px]',
  lg: 'w-full sm:w-[720px]',
  xl: 'w-full sm:w-[880px] lg:w-[960px]',
};

export function FormDrawer({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  saving,
  saveDisabled,
  error,
  dirty,
  width,
  size = 'lg',
  children,
}: FormDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prevActive = document.activeElement as HTMLElement | null;
    // Focus the first FIELD, never a button — the header × is the first button in
    // DOM order, and focusing it made Enter close the drawer on open (Tally users
    // press Enter reflexively). Fall back to Save only if the form has no fields.
    const firstFocusable =
      drawerRef.current?.querySelector<HTMLElement>('input, select, textarea') ??
      drawerRef.current?.querySelector<HTMLElement>('button[type="submit"]');
    firstFocusable?.focus();
    return () => prevActive?.focus();
  }, [open]);

  function tryClose() {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onClose();
  }

  useHotkeys(
    'escape',
    (e) => { if (open) { e.preventDefault(); tryClose(); } },
    { enableOnFormTags: ['input', 'textarea', 'select'] },
    [open, dirty],
  );

  useHotkeys(
    'ctrl+a, ctrl+s',
    (e) => {
      if (open) {
        e.preventDefault();
        if (!saveDisabled && !saving) onSubmit();
      }
    },
    { enableOnFormTags: ['input', 'textarea', 'select'] },
    [open, saving, saveDisabled],
  );

  if (!open) return null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!saveDisabled && !saving) onSubmit();
  }

  // Portal to <body>: an ancestor with backdrop-filter/transform (e.g. the sticky
  // blurred top bar) becomes the containing block for position:fixed and collapses
  // the drawer into it — the "half-baked" Add Company screen. Portaling makes the
  // drawer immune to wherever it's mounted.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="orz-drawer fixed inset-0 z-50"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={tryClose}
        className="absolute inset-0 bg-obsidian/40 backdrop-blur-[3px] transition-opacity"
      />

      <div
        ref={drawerRef}
        className={cn(
          'orz-drawer absolute right-0 top-0 flex h-full flex-col bg-snow shadow-hero',
          'border-l border-mist',
          'animate-slide-in',
          width ? '' : SIZE_STYLES[size],
        )}
        style={width ? { width } : undefined}
      >
        {/* Signature Orenza gradient stripe */}
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg,var(--color-primary) 0%,var(--color-primary) 55%,var(--color-warm) 100%)' }}
          aria-hidden
        />
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-mist px-6 py-5">
            <div className="flex flex-col gap-1">
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.12em]">
                  New
                </span>
                <h2
                  className="font-display text-display-md font-semibold text-obsidian"
                  style={{ letterSpacing: 'var(--tracking-display)' }}
                >
                  {title}
                </h2>
              </div>
              {subtitle && (
                <p className="max-w-prose text-body-sm text-slate">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              tabIndex={-1}
              onClick={tryClose}
              className="rounded-md h-8 w-8 grid place-items-center text-cool hover:text-obsidian hover:bg-haze transition"
              aria-label="Close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {error && (
              <div className="mb-5 rounded-md border border-crimson/40 bg-crimson/[0.05] px-4 py-3 text-body-sm text-crimson">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-5">{children}</div>
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-mist bg-haze/70 px-6 py-4">
            <div className="flex items-center gap-2 text-caption text-slate">
              <KbdChip>Ctrl S</KbdChip> Save · <KbdChip>Esc</KbdChip> Close
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={tryClose}>Cancel</Button>
              <Button type="submit" loading={saving} disabled={saveDisabled}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
